/* ExplainerHero — the same 1:15 cinematic explainer used inside the
 * Game Bible, but styled as a hero card for the landing / login page
 * so new visitors watch the story before signing up.
 *
 * - Poster-first, tap-to-play (autoplay off — respects data plans).
 * - Language-baked captions: renders /game-explainer-{en|es|fr}.mp4 to
 *   match the current i18n locale (falls back to English).
 * - Native <video controls> for reliable behaviour on iOS Safari.
 */
import { useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';

const EXPLAINER_LANGS = ['en', 'es', 'fr'];
const POSTER = '/game-explainer-poster.jpg';

function pickExplainer(lang) {
  const short = String(lang || 'en').toLowerCase().split('-')[0];
  const chosen = EXPLAINER_LANGS.includes(short) ? short : 'en';
  return `/game-explainer-${chosen}.mp4`;
}

const COPY = {
  en: {
    eyebrow: 'Watch the 60-second story',
    title: 'Get outside. Discover wildlife.',
    sub: 'From capture to card to arena — see how WildQuest works.',
    tap: 'Tap to play',
  },
  es: {
    eyebrow: 'Mira la historia en 60 segundos',
    title: 'Sal a la naturaleza. Descubre la vida salvaje.',
    sub: 'De la captura a la carta y a la arena — así funciona WildQuest.',
    tap: 'Toca para reproducir',
  },
  fr: {
    eyebrow: 'Regardez l\'histoire en 60 secondes',
    title: 'Sortez. Découvrez la nature sauvage.',
    sub: 'De la capture à la carte et à l\'arène — voici comment WildQuest fonctionne.',
    tap: 'Touchez pour lire',
  },
};

export default function ExplainerHero() {
  const { lang } = useI18n();
  const short = EXPLAINER_LANGS.includes(String(lang || 'en').toLowerCase().split('-')[0])
    ? String(lang).toLowerCase().split('-')[0]
    : 'en';
  const copy = COPY[short];
  const src = pickExplainer(lang);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const start = () => {
    setPlaying(true);
    setTimeout(() => { ref.current?.play?.().catch(() => {}); }, 20);
  };

  return (
    <div
      data-testid="explainer-hero"
      className="relative w-full max-w-2xl mx-auto rounded-3xl overflow-hidden border border-emerald-400/25 shadow-2xl shadow-black/50 bg-black aspect-video"
    >
      {!playing ? (
        <button
          type="button"
          onClick={start}
          data-testid="explainer-hero-play"
          aria-label={copy.tap}
          className="group absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${POSTER})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="h-20 w-20 rounded-full bg-emerald-500/95 flex items-center justify-center border-2 border-white/30 shadow-2xl group-hover:scale-110 transition-transform relative">
              <span className="absolute inset-0 rounded-full ring-2 ring-emerald-200 animate-ping opacity-40" />
              <Play className="w-9 h-9 text-black ml-1 relative" fill="currentColor" strokeWidth={0} />
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-200">{copy.eyebrow}</span>
            <span className="font-serif text-3xl sm:text-4xl leading-tight text-white">{copy.title}</span>
            <span className="text-sm text-white/80 max-w-md">{copy.sub}</span>
          </span>
        </button>
      ) : (
        <video
          ref={ref}
          key={src}
          data-testid="explainer-hero-video"
          src={src}
          poster={POSTER}
          controls
          playsInline
          preload="metadata"
          className="w-full h-full bg-black"
        />
      )}
    </div>
  );
}
